import React from "react";

/**
 * Uploading state (LU-A10 + LU-A7): determinate progress bar driven by the page's
 * axios `onUploadProgress`. Full-width, flat house style. Presentational only.
 */
interface UploadProgressPanelProps {
  /** 0..100 */
  percent: number;
  fileCount?: number;
  bytesUploaded?: number;
  bytesTotal?: number;
  /** When the user chose to strip identifiers, surface it in the label. */
  stripping?: boolean;
}

function fmtMb(bytes?: number): string | null {
  if (bytes == null) return null;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const UploadProgressPanel: React.FC<UploadProgressPanelProps> = ({
  percent,
  fileCount,
  bytesUploaded,
  bytesTotal,
  stripping = false,
}) => {
  const pct = Math.max(0, Math.min(100, Math.round(percent)));
  const now = fmtMb(bytesUploaded);
  const total = fmtMb(bytesTotal);
  return (
    <div data-testid="upload-progress">
      <div className="mb-2.5 flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
          {stripping ? "Stripping + uploading" : "Uploading"}
        </span>
        <span className="font-mono text-[28px] font-medium leading-none tracking-tight tabular-nums">
          {pct}
          <span className="text-base text-fg-dim">%</span>
        </span>
      </div>
      <div
        className="h-1 w-full overflow-hidden rounded-[2px] bg-surface-2"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full bg-signal transition-[width] duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-2.5 flex justify-between font-mono text-[10px] tracking-[0.04em] text-fg-dim">
        <span>{now ?? `${pct}%`}</span>
        <span>
          {total ?? ""}
          {fileCount != null ? `${total ? " · " : ""}${fileCount} files` : ""}
        </span>
      </div>
    </div>
  );
};
